/**
 * Component gallery + Phase 2 sign-off page for the app-shell rebuild, per
 * assets/handoffs/design_handoff_app_shell. Registered at /shell-demo,
 * manual QA only, not product UI.
 *
 * Phase 1 (component-by-component, no app wiring): every section down to
 * "Sidebar (assembled)" shows one component and its states individually,
 * mirroring theme/untitled/Demo/ThemeDemo.jsx.
 *
 * Phase 2 (the sign-off gate): the final "Phase 2 — Assembled shell states"
 * section below reproduces the four full shell states — default,
 * impersonating, collapsed, mobile — side by side, composed from the same
 * Phase 1 components at a real fixed frame height (832px, matching
 * App shell.dc.html's `.app`), so the sidebar's internal nav-scroll and
 * pinned 136px footer get exercised the way they will inside the real
 * viewport-height AppShell, not just inside Phase 1's fixed-height preview
 * boxes. Review this against App shell.dc.html before starting Phase 3.
 */
import React, { useState } from "react";
import { MemoryRouter } from "react-router-dom";
import Button from "../../../theme/untitled/Button/Button";
import Icon from "../../../theme/untitled/icons/Icon";
import SidebarNavItem from "../SidebarNavItem/SidebarNavItem";
import SidebarNavParent from "../SidebarNavParent/SidebarNavParent";
import WorkspaceSwitcher from "../WorkspaceSwitcher/WorkspaceSwitcher";
import AccountRow from "../AccountRow/AccountRow";
import AccountMenu from "../AccountMenu/AccountMenu";
import ImpersonationBar from "../ImpersonationBar/ImpersonationBar";
import Sidebar from "../Sidebar/Sidebar";
import PageHeader from "../PageHeader/PageHeader";
import SettingsTabs from "../SettingsTabs/SettingsTabs";
import MobileTopBar from "../MobileTopBar/MobileTopBar";
import NavDrawer from "../NavDrawer/NavDrawer";
import {
  account,
  workspaces,
  currentWorkspace,
  navSections,
  alerts,
  impersonation,
  workspaceSettingsTabs,
  accountSettingsTabs,
} from "../fixtures";
import styles from "./ShellDemo.module.scss";

const Section = ({ title, children }) => (
  <section className={styles.section}>
    <h2 className={styles.sectionTitle}>{title}</h2>
    {children}
  </section>
);

const Row = ({ label, children }) => (
  <div className={styles.row}>
    <span className={styles.rowLabel}>{label}</span>
    <div className={styles.rowContent}>{children}</div>
  </div>
);

const noop = () => {};

const ShellDemo = () => {
  const [sidebarAlerts, setSidebarAlerts] = useState(alerts);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [impersonating, setImpersonating] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const dismissAlert = (id) => setSidebarAlerts((prev) => prev.filter((a) => a.id !== id));

  const renderAccountMenu = (close) => (
    <AccountMenu
      isAdmin
      adminNavOpen={false}
      onToggleAdminNav={noop}
      onLogout={close}
      onClose={close}
    />
  );

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>App shell — component gallery</h1>
        <p className={styles.subtitle}>
          react-app/src/apps/admin/templates/AppShell — spec at
          assets/handoffs/design_handoff_app_shell
        </p>
      </header>

      <Section title="SidebarNavItem">
        <Row label="rest / active / child">
          <ul className={styles.navPreviewList}>
            <SidebarNavItem label="Dashboard" icon="HomeLine" to="/dashboard" />
            <SidebarNavItem label="Integrations" icon="Dataflow04" to="/integrations" active />
            <SidebarNavItem label="Calendars" to="/calendars" child />
          </ul>
        </Row>
        <Row label="collapsed (icon + tooltip, hover to see)">
          <ul className={styles.navPreviewList} style={{ width: 72 }}>
            <SidebarNavItem label="Dashboard" icon="HomeLine" to="/dashboard" collapsed />
            <SidebarNavItem label="Integrations" icon="Dataflow04" to="/integrations" active collapsed />
          </ul>
        </Row>
      </Section>

      <Section title="SidebarNavParent + NavFlyout">
        <Row label="expanded (inline disclosure)">
          <ul className={styles.navPreviewList}>
            <SidebarNavParent
              label={navSections.widgets.label}
              icon={navSections.widgets.icon}
              children={navSections.widgets.children}
              activeKey="calendar"
            />
          </ul>
        </Row>
        <Row label="collapsed (click icon to open flyout)">
          <ul className={styles.navPreviewList} style={{ width: 72 }}>
            <SidebarNavParent
              label={navSections.widgets.label}
              icon={navSections.widgets.icon}
              children={navSections.widgets.children}
              activeKey="calendar"
              collapsed
            />
          </ul>
        </Row>
      </Section>

      <Section title="WorkspaceSwitcher">
        <Row label="expanded (click to open)">
          <div className={styles.boxed280}>
            <WorkspaceSwitcher workspaces={workspaces} current={currentWorkspace} onSwitch={noop} />
          </div>
        </Row>
        <Row label="collapsed">
          <div className={styles.boxed72}>
            <WorkspaceSwitcher workspaces={workspaces} current={currentWorkspace} onSwitch={noop} collapsed />
          </div>
        </Row>
        <Row label="disabled (impersonating)">
          <div className={styles.boxed280}>
            <WorkspaceSwitcher workspaces={workspaces} current={currentWorkspace} onSwitch={noop} disabled />
          </div>
        </Row>
      </Section>

      <Section title="AccountRow + AccountMenu">
        <Row label="rest (click to open)">
          <div className={styles.boxed280}>
            <AccountRow account={account} renderMenu={renderAccountMenu} />
          </div>
        </Row>
        <Row label="impersonating">
          <div className={styles.boxed280}>
            <AccountRow account={account} impersonating renderMenu={renderAccountMenu} />
          </div>
        </Row>
        <Row label="collapsed">
          <div className={styles.boxed72}>
            <AccountRow account={account} collapsed renderMenu={renderAccountMenu} />
          </div>
        </Row>
      </Section>

      <Section title="ImpersonationBar">
        <Row label="default">
          <ImpersonationBar accountName="Northwind Traders" onStop={noop} />
        </Row>
        <Row label="long account name (truncates)">
          <ImpersonationBar
            accountName="Northwind Traders International Event Management & Ticketing Group"
            onStop={noop}
          />
        </Row>
      </Section>

      <Section title="Sidebar (assembled)">
        <Row label="toggle">
          <Button hierarchy="secondaryGray" size="sm" onClick={() => setSidebarCollapsed((v) => !v)}>
            {sidebarCollapsed ? "Expand" : "Collapse"}
          </Button>
          <Button hierarchy="secondaryGray" size="sm" onClick={() => setImpersonating((v) => !v)}>
            {impersonating ? "Stop impersonating" : "Simulate impersonating"}
          </Button>
        </Row>
        <div className={styles.sidebarPreview}>
          <Sidebar
            collapsed={sidebarCollapsed}
            onToggleCollapse={() => setSidebarCollapsed((v) => !v)}
            workspaces={workspaces}
            currentWorkspace={currentWorkspace}
            onSwitchWorkspace={noop}
            nav={navSections}
            activeKey="calendar"
            alerts={sidebarAlerts}
            onDismissAlert={dismissAlert}
            account={account}
            impersonating={impersonating}
            renderAccountMenu={renderAccountMenu}
          />
        </div>
      </Section>

      <Section title="PageHeader">
        <Row label="with breadcrumb + actions">
          <div className={styles.boxedContent}>
            <PageHeader
              breadcrumbs={[{ label: "Widgets", to: "/widgets" }, { label: "Calendars" }]}
              title="Calendars"
              subtitle="Six calendars, four published."
              actions={
                <Button size="sm" iconLeading={<Icon name="Plus" />}>
                  New calendar
                </Button>
              }
            />
          </div>
        </Row>
        <Row label="single level — no breadcrumb">
          <div className={styles.boxedContent}>
            <PageHeader title="Dashboard" subtitle="Everything running under the Professional plan." />
          </div>
        </Row>
        <Row label="compact (builder-type page)">
          <div className={styles.boxedContent}>
            <PageHeader
              compact
              breadcrumbs={[{ label: "Widgets", to: "/widgets" }, { label: "test" }]}
              title="test"
              actions={
                <>
                  <Button hierarchy="secondaryGray" size="sm">
                    Preview
                  </Button>
                  <Button hierarchy="secondaryGray" size="sm" iconLeading={<Icon name="DotsVertical" />} />
                </>
              }
            />
          </div>
        </Row>
      </Section>

      <Section title="SettingsTabs">
        <Row label="workspace settings (5 tabs)">
          <div className={styles.boxedContent}>
            <MemoryRouter initialEntries={[workspaceSettingsTabs[0].to]}>
              <SettingsTabs tabs={workspaceSettingsTabs} />
            </MemoryRouter>
          </div>
        </Row>
        <Row label="account settings (2 tabs)">
          <div className={styles.boxedContent}>
            <MemoryRouter initialEntries={[accountSettingsTabs[0].to]}>
              <SettingsTabs tabs={accountSettingsTabs} />
            </MemoryRouter>
          </div>
        </Row>
      </Section>

      <Section title="MobileTopBar + NavDrawer">
        <Row label="trigger">
          <Button hierarchy="secondaryGray" size="sm" onClick={() => setDrawerOpen(true)}>
            Open drawer
          </Button>
        </Row>
        <div className={styles.mobilePreview}>
          <MobileTopBar workspaceInitials={currentWorkspace.initials} onMenuClick={() => setDrawerOpen(true)} />
        </div>
        <NavDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          sidebar={{
            workspaces,
            currentWorkspace,
            onSwitchWorkspace: noop,
            nav: navSections,
            activeKey: "calendar",
            alerts: sidebarAlerts,
            onDismissAlert: dismissAlert,
            account,
            impersonating,
            renderAccountMenu,
          }}
        />
      </Section>

      <Section title="Phase 2 — Assembled shell states (sign-off)">
        <p className={styles.sectionNote}>
          The four full shell states side by side, per Phase 2 of the handoff. Review against{" "}
          <code>App shell.dc.html</code> before starting Phase 3.
        </p>

        <Row label="default">
          <div className={styles.appFrame}>
            <div className={styles.appRow}>
              <Sidebar
                workspaces={workspaces}
                currentWorkspace={currentWorkspace}
                onSwitchWorkspace={noop}
                nav={navSections}
                activeKey="dashboard"
                alerts={sidebarAlerts}
                onDismissAlert={dismissAlert}
                account={account}
                renderAccountMenu={renderAccountMenu}
              />
              <div className={styles.appContent}>
                <PageHeader
                  title="Dashboard"
                  subtitle="Everything running under the Professional plan."
                  actions={
                    <Button size="sm" iconLeading={<Icon name="Plus" />}>
                      New widget
                    </Button>
                  }
                />
                <div className={styles.ghostCards}>
                  <div className={styles.ghostCard} />
                  <div className={styles.ghostCard} />
                </div>
              </div>
            </div>
          </div>
        </Row>

        <Row label="impersonating">
          <div className={styles.appFrame}>
            <ImpersonationBar accountName={impersonation.accountName} onStop={noop} />
            <div className={styles.appRow}>
              <Sidebar
                workspaces={workspaces}
                currentWorkspace={currentWorkspace}
                onSwitchWorkspace={noop}
                nav={navSections}
                activeKey="calendar"
                alerts={sidebarAlerts}
                onDismissAlert={dismissAlert}
                account={account}
                impersonating
                renderAccountMenu={renderAccountMenu}
              />
              <div className={styles.appContent}>
                <PageHeader
                  breadcrumbs={[{ label: "Widgets", to: "/widgets" }, { label: "Calendars" }]}
                  title="Calendars"
                  subtitle="Six calendars, four published."
                  actions={
                    <Button size="sm" iconLeading={<Icon name="Plus" />}>
                      New calendar
                    </Button>
                  }
                />
                <div className={styles.ghostCards}>
                  <div className={styles.ghostCard} />
                </div>
              </div>
            </div>
          </div>
        </Row>

        <Row label="collapsed — corner-collision check">
          <div className={styles.appFrame}>
            <div className={styles.appRow}>
              <Sidebar
                collapsed
                workspaces={workspaces}
                currentWorkspace={currentWorkspace}
                onSwitchWorkspace={noop}
                nav={navSections}
                activeKey="calendar"
                alerts={sidebarAlerts}
                onDismissAlert={dismissAlert}
                account={account}
                renderAccountMenu={renderAccountMenu}
              />
              <div className={styles.appContent}>
                <PageHeader
                  compact
                  breadcrumbs={[{ label: "Widgets", to: "/widgets" }, { label: "test" }]}
                  title="test"
                  actions={
                    <>
                      <Button hierarchy="secondaryGray" size="sm">
                        Preview
                      </Button>
                      <Button hierarchy="secondaryGray" size="sm" iconLeading={<Icon name="DotsVertical" />} />
                    </>
                  }
                />
                <div className={styles.builderMock}>
                  <div className={styles.builderCanvas} />
                  <div className={styles.builderFooter}>
                    <Button hierarchy="secondaryGray" size="sm">
                      Discard
                    </Button>
                    <Button size="sm">Save changes</Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Row>

        <Row label="mobile — 390×844">
          <div className={styles.mobileFrame}>
            <MobileTopBar workspaceInitials={currentWorkspace.initials} onMenuClick={noop} />
            <div className={styles.mobileBody}>
              <PageHeader title="Dashboard" subtitle="Everything running under the Professional plan." />
              <div className={styles.ghostCard} />
              <div className={styles.ghostCard} />
            </div>
          </div>
        </Row>
      </Section>
    </div>
  );
};

export default ShellDemo;
